/**
 * W-13 订单详情(WScreen-13)。按 viewerRole × state × subState 渲染 CTA(唯一真源
 * state-machine.md §四)。买家「确认完成」接线(修 App drift②);卖家同意/拒绝取消。
 * 字段层硬隔离:CommissionBreakdownCard 仅卖家。视觉真源 design/order-detail.html。
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  completeOrder,
  getOrderDetail,
  type BuyerOrderDetail,
  type CancellationContext,
  type OrderDetail,
  type SellerOrderDetail,
} from '@/api/order';
import { agreeCancellation, rejectCancellation, REASON_LABELS } from '@/api/aftersales';
import {
  BADGE_CLASS,
  errorCode,
  errorMessage,
  formatAmount,
  formatDateTime,
  resolveStateBadge,
} from '@/lib/orderState';
import { useCountdown } from '@/lib/useCountdown';
import { toast } from '@/components/ui/toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const REJECT_MIN = 10;
const REJECT_MAX = 500;

export function OrderDetailPage() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['order-detail', orderId],
    queryFn: () => getOrderDetail(orderId),
    enabled: orderId !== '',
    retry: 0,
  });

  const [agreeOpen, setAgreeOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const invalidate = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['my-orders'] });
  };

  const completeMut = useMutation({
    mutationFn: () => completeOrder(orderId),
    onSuccess: () => {
      toast.success('已确认完成');
      invalidate();
    },
    onError: (err) => {
      if (errorCode(err) === '030103') refetch();
      toast.error(errorMessage(err, '操作失败,请重试'));
    },
  });

  const agreeMut = useMutation({
    mutationFn: () => agreeCancellation(orderId),
    onSuccess: () => {
      setAgreeOpen(false);
      toast.success('已同意取消,款项将原路退回');
      invalidate();
    },
    onError: (err) => {
      setAgreeOpen(false);
      toast.error(mapSellerResolveError(err));
    },
  });

  const rejectMut = useMutation({
    mutationFn: (rebuttalText: string) => rejectCancellation(orderId, rebuttalText),
    onSuccess: () => {
      setRejectOpen(false);
      toast.success('已提交拒绝,订单进入平台仲裁');
      invalidate();
    },
    onError: (err) => {
      toast.error(mapSellerResolveError(err));
    },
  });

  if (isLoading) {
    return (
      <div className="inner section">
        <div className="state" data-testid="order-detail-loading">
          <p className="muted sm" style={{ color: 'var(--color-primary)' }}>
            加载中…
          </p>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    const code = errorCode(error);
    const msg =
      code === '030301'
        ? '订单不存在'
        : code === '030302'
          ? '你无权查看此订单'
          : '网络异常,请稍后重试';
    return (
      <div className="inner section">
        <div className="state" data-testid={`order-detail-error-${code}`}>
          <p className="sm" style={{ color: 'var(--color-destructive)' }}>
            {msg}
          </p>
          <button type="button" className="btn btn-secondary mt-4" onClick={() => refetch()}>
            点此重试
          </button>
        </div>
      </div>
    );
  }

  const isBuyer = data.viewerRole === 'BUYER';
  const badgeVariant = resolveStateBadge(data.state, data.subState).variant;

  return (
    <div className="inner section">
      <div className="sm muted mb-6">订单详情</div>
      <div className="row between mb-6">
        <div className="row gap-4">
          <h1 className="h2">订单详情</h1>
          <span className={BADGE_CLASS[badgeVariant]}>{heroText(data)}</span>
        </div>
        <span className="sm muted">订单号 {data.orderId}</span>
      </div>

      <div className="detail-cols">
        {/* 左:时间线 + 订单信息 */}
        <div>
          <OrderTimelineCard detail={data} />
          <OrderInfoCard detail={data} isBuyer={isBuyer} />
        </div>

        {/* 右:操作卡 + (卖家)分账卡 */}
        <aside className="sticky-cta">
          <div className="card card-pad">
            <h3 className="h3 mb-4">订单操作</h3>
            <div className="row between mb-2">
              <span className="muted sm">{isBuyer ? '合计支付' : '订单总额'}</span>
              <span className="price" style={{ fontSize: 'var(--text-3xl-size)' }}>
                {formatAmount(data.amount)}
              </span>
            </div>
            <p
              className="xs muted mb-6"
              style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: 'var(--space-4)' }}
            >
              交易确认完成后,平台将款项结算给卖家。
            </p>

            {isBuyer ? (
              <BuyerActions
                detail={data as BuyerOrderDetail}
                completing={completeMut.isPending}
                onComplete={() => completeMut.mutate()}
                onRequestCancel={() => navigate(`/cancellation/${orderId}`)}
                onRequestAftersale={() =>
                  navigate(`/cancellation/${orderId}`, { state: { aftersale: true } })
                }
                onReview={() => navigate(`/review/${orderId}`)}
                onContact={() =>
                  navigate('/conversations', {
                    state: {
                      peerUserId: (data as BuyerOrderDetail).seller?.sellerId,
                      seedProductId: data.product?.productId,
                    },
                  })
                }
              />
            ) : (
              <SellerActions
                detail={data as SellerOrderDetail}
                busy={agreeMut.isPending || rejectMut.isPending}
                onAgree={() => setAgreeOpen(true)}
                onReject={() => setRejectOpen(true)}
                onContact={() =>
                  navigate('/conversations', {
                    state: {
                      peerUserId: (data as SellerOrderDetail).buyer?.buyerId,
                      seedProductId: data.product?.productId,
                    },
                  })
                }
              />
            )}
          </div>

          {/* 分账明细:有售后/取消待处理(待卖家响应 / 仲裁中)时不展示——此时结算金额未定,
              展示会误导卖家。售后处置结束回到确定态后再显示。 */}
          {!isBuyer &&
            data.subState !== 'PENDING_SELLER_RESPONSE' &&
            data.subState !== 'IN_ARBITRATION' && (
              <CommissionBreakdownCard detail={data as SellerOrderDetail} />
            )}
        </aside>
      </div>

      {/* 卖家同意取消:二次确认(C-5) */}
      <ConfirmDialog
        open={agreeOpen}
        title="确认同意取消订单?"
        description="同意后订单将取消并退款,是否继续?"
        confirmLabel="确认同意"
        confirmVariant="destructive"
        submitting={agreeMut.isPending}
        onConfirm={() => agreeMut.mutate()}
        onCancel={() => setAgreeOpen(false)}
      />

      {/* 卖家拒绝取消:填理由 → 仲裁 */}
      <SellerRejectDialog
        open={rejectOpen}
        submitting={rejectMut.isPending}
        onSubmit={(text) => rejectMut.mutate(text)}
        onCancel={() => setRejectOpen(false)}
      />
    </div>
  );
}

/* ─────────────── 时间线 ─────────────── */
function OrderTimelineCard({ detail }: { detail: OrderDetail }) {
  const nodes = detail.timeline ?? [];
  return (
    <div className="card card-pad">
      <h3 className="h3 mb-4">订单状态</h3>
      {nodes.length === 0 ? (
        <p className="xs muted">暂无状态记录</p>
      ) : (
        <ul className="timeline">
          {nodes.map((n, i) => (
            <li key={`${n.state}-${n.subState}-${i}`} className={n.occurred ? 'done' : ''}>
              <div className="t">{n.label}</div>
              <div className="ts">{n.occurredAt ? formatDateTime(n.occurredAt) : '—'}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────────── 订单信息 ─────────────── */
function OrderInfoCard({ detail, isBuyer }: { detail: OrderDetail; isBuyer: boolean }) {
  const p = detail.product;
  const counterparty = isBuyer
    ? (detail as BuyerOrderDetail).seller
    : (detail as SellerOrderDetail).buyer;
  const counterpartyName = counterparty?.nickname ?? '我行用户';
  return (
    <div className="card card-pad mt-8">
      <h3 className="h3 mb-4">订单信息</h3>
      <div className="row gap-4 mb-6" style={{ alignItems: 'flex-start' }}>
        <div
          className="avatar avatar-lg"
          style={{ borderRadius: 'var(--radius-md)', width: 80, height: 80, overflow: 'hidden' }}
        >
          {p?.coverImageUrl ? (
            <img
              src={p.coverImageUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            '封面'
          )}
        </div>
        <div className="col gap-2" style={{ flex: 1 }}>
          <div style={{ fontWeight: 'var(--weight-medium)' }}>{p?.productTitle || '商品已下架'}</div>
          <div className="price">{formatAmount(detail.amount)}</div>
        </div>
      </div>
      <div className="col gap-3">
        <div className="row between">
          <span className="muted sm">订单号</span>
          <span className="sm">{detail.orderId}</span>
        </div>
        <div className="row between">
          <span className="muted sm">下单时间</span>
          <span className="sm">{formatDateTime(detail.createdAt)}</span>
        </div>
        <div className="row between">
          <span className="muted sm">{isBuyer ? '卖家' : '买家'}</span>
          <span className="sm">{counterpartyName}</span>
        </div>
        {detail.remark && (
          <div className="row between">
            <span className="muted sm">买家备注</span>
            <span className="sm" style={{ maxWidth: '60%', textAlign: 'right' }}>
              {detail.remark}
            </span>
          </div>
        )}
      </div>
      {detail.cancelPolicyText && (
        <div
          className="mt-6 xs muted"
          style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-4)' }}
        >
          取消政策:{detail.cancelPolicyText}
        </div>
      )}
    </div>
  );
}

/* ─────────────── 买家 CTA(state × subState) ─────────────── */
function BuyerActions({
  detail,
  completing,
  onComplete,
  onRequestCancel,
  onRequestAftersale,
  onReview,
  onContact,
}: {
  detail: BuyerOrderDetail;
  completing: boolean;
  onComplete: () => void;
  onRequestCancel: () => void;
  onRequestAftersale: () => void;
  onReview: () => void;
  onContact: () => void;
}) {
  const { state, subState } = detail;

  if (state === 'IN_PROGRESS' && subState === 'NONE') {
    // ★接线「确认完成」(修 App drift②)+ 申请取消 + 联系对方。
    return (
      <>
        <button
          type="button"
          className="btn btn-primary btn-lg btn-block mb-3"
          disabled={completing}
          onClick={onComplete}
        >
          {completing ? '处理中…' : '确认完成'}
        </button>
        <button type="button" className="btn btn-secondary btn-lg btn-block mb-3" onClick={onContact}>
          联系卖家
        </button>
        <button type="button" className="btn btn-ghost btn-block" onClick={onRequestCancel}>
          申请取消订单
        </button>
      </>
    );
  }

  if (state === 'IN_PROGRESS' && subState === 'PENDING_SELLER_RESPONSE') {
    return (
      <>
        {detail.cancellationContext && (
          <BuyerCancellationRecordCard ctx={detail.cancellationContext} variant="cancel" />
        )}
        <FooterHint>已申请取消,等待卖家响应</FooterHint>
      </>
    );
  }

  if (state === 'IN_PROGRESS' && subState === 'IN_ARBITRATION') {
    return (
      <>
        {detail.cancellationContext && (
          <BuyerCancellationRecordCard ctx={detail.cancellationContext} variant="cancel" />
        )}
        <FooterHint>{arbitrationText(detail.arbitrationContext?.slaDeadlineAt, '已进入平台仲裁')}</FooterHint>
      </>
    );
  }

  // 售后进行中(COMPLETED 子态):已申请 → 等卖家;仲裁 → 平台处理中。买家能看到自己提交的售后单。
  if (state === 'COMPLETED' && subState === 'PENDING_SELLER_RESPONSE') {
    return (
      <>
        {detail.cancellationContext && (
          <BuyerCancellationRecordCard ctx={detail.cancellationContext} variant="aftersale" />
        )}
        <FooterHint>已申请售后,等待卖家响应</FooterHint>
      </>
    );
  }
  if (state === 'COMPLETED' && subState === 'IN_ARBITRATION') {
    return (
      <>
        {detail.cancellationContext && (
          <BuyerCancellationRecordCard ctx={detail.cancellationContext} variant="aftersale" />
        )}
        <FooterHint>
          {arbitrationText(detail.arbitrationContext?.slaDeadlineAt, '售后处理中,平台仲裁中')}
        </FooterHint>
      </>
    );
  }

  if (state === 'COMPLETED') {
    // 评价与售后正交(已评价订单仍可在确认完成后 7 天内申请售后)。
    const canReview = detail.reviewable || detail.reviewed;
    const canAftersale = detail.aftersaleable === true;
    if (!canReview && !canAftersale) {
      return <FooterHint>订单当前无可执行操作。</FooterHint>;
    }
    return (
      <>
        {canReview && (
          <button
            type="button"
            className={`btn btn-primary btn-lg btn-block${canAftersale ? ' mb-3' : ''}`}
            disabled={!detail.reviewable}
            onClick={onReview}
          >
            {detail.reviewable ? '去评价' : '已评价'}
          </button>
        )}
        {canAftersale && (
          <button type="button" className="btn btn-ghost btn-block" onClick={onRequestAftersale}>
            申请售后
          </button>
        )}
      </>
    );
  }

  return <FooterHint>订单当前无可执行操作。</FooterHint>;
}

/* ─────────────── 卖家 CTA(state × subState) ─────────────── */
function SellerActions({
  detail,
  busy,
  onAgree,
  onReject,
  onContact,
}: {
  detail: SellerOrderDetail;
  busy: boolean;
  onAgree: () => void;
  onReject: () => void;
  onContact: () => void;
}) {
  const { state, subState } = detail;

  // 待卖家响应:覆盖 IN_PROGRESS(进行中取消)与 COMPLETED(完成后售后,Story 15.2)——
  // 两者 subState 同为 PENDING_SELLER_RESPONSE,后端同一 agree/reject 端点均支持(见
  // AgreeCancellationUseCase「源主态 IN_PROGRESS 或 COMPLETED」)。走查前缺 COMPLETED 分支
  // 导致卖家在售后单看到"无可执行操作"。
  if (subState === 'PENDING_SELLER_RESPONSE') {
    const ctx = detail.cancellationContext ?? null;
    const isAftersale = state === 'COMPLETED';
    return (
      <>
        {ctx ? (
          <div
            className="card"
            style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}
          >
            <div className="sm" style={{ fontWeight: 'var(--weight-medium)' }}>
              {isAftersale ? '买家售后原因' : '买家原因'}:{REASON_LABELS[ctx.reasonCode] ?? '其他'}
            </div>
            <p className="sm muted mt-2">{ctx.description}</p>
            <SellerResponseDeadline deadlineAt={ctx.deadlineAt} />
          </div>
        ) : (
          <p className="sm muted mb-4">买家陈述加载中…</p>
        )}
        <button
          type="button"
          className="btn btn-primary btn-lg btn-block mb-3"
          disabled={busy}
          onClick={onAgree}
        >
          {isAftersale ? '同意售后' : '同意取消'}
        </button>
        <button type="button" className="btn btn-ghost btn-block mb-3" disabled={busy} onClick={onReject}>
          {isAftersale ? '拒绝售后' : '拒绝取消'}
        </button>
        <button type="button" className="btn btn-secondary btn-block" onClick={onContact}>
          联系买家
        </button>
      </>
    );
  }

  if (subState === 'IN_ARBITRATION') {
    return (
      <FooterHint>
        {arbitrationText(
          detail.arbitrationContext?.slaDeadlineAt,
          state === 'COMPLETED' ? '售后处理中,平台仲裁中' : '已拒绝取消,平台仲裁中',
        )}
      </FooterHint>
    );
  }

  if (state === 'IN_PROGRESS' && subState === 'NONE') {
    return (
      <>
        <FooterHint>订单进行中,完成由买家确认(见右侧分账 T+7)。</FooterHint>
        <button
          type="button"
          className="btn btn-secondary btn-block mt-3"
          onClick={onContact}
        >
          联系买家
        </button>
      </>
    );
  }

  return <FooterHint>订单当前无可执行操作。</FooterHint>;
}

/* ─────────────── 卖家分账卡(仅卖家;FR-C13) ─────────────── */
function CommissionBreakdownCard({ detail }: { detail: SellerOrderDetail }) {
  const t7 = detail.t7CountdownSec;
  const t7Text =
    detail.state === 'COMPLETED' && t7 != null
      ? t7 <= 0
        ? '已逾期'
        : formatDaysHours(t7)
      : '交易完成后启动';
  const t7Primary = detail.state === 'COMPLETED' && t7 != null && t7 > 0 && t7 < 86400;
  return (
    <div className="card card-pad mt-6">
      <h3 className="h3 mb-4">分账明细</h3>
      <div className="row between mb-3">
        <span className="muted sm">订单总额</span>
        <span className="sm">{formatAmount(detail.amount)}</span>
      </div>
      <div className="row between mb-3">
        <span className="muted sm">平台抽佣 (5%)</span>
        <span className="sm">-{formatAmount(detail.commissionAmount)}</span>
      </div>
      <div className="row between mb-3">
        <span className="muted sm">我将到账</span>
        <span className="sm" style={{ color: 'var(--color-primary)', fontWeight: 'var(--weight-semibold)' }}>
          {formatAmount(detail.sellerAmount)}
        </span>
      </div>
      <div className="row between mb-3">
        <span className="muted sm">T+7 打款倒计时</span>
        <span className="sm" style={{ color: t7Primary ? 'var(--color-primary)' : undefined }}>
          {t7Text}
        </span>
      </div>
      <div className="row between">
        <span className="muted sm">已打款金额</span>
        <span className="sm">{detail.settledAmount > 0 ? formatAmount(detail.settledAmount) : '—'}</span>
      </div>
    </div>
  );
}

/* ─────────────── 卖家拒绝理由对话框 ─────────────── */
function SellerRejectDialog({
  open,
  submitting,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  submitting: boolean;
  onSubmit: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const close = () => {
    setText('');
    onCancel();
  };
  // Esc = 取消(与 C-5 契约一致);提交中不可关。
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);
  if (!open) return null;
  const trimmed = text.trim().length;
  const canSubmit = trimmed >= REJECT_MIN && trimmed <= REJECT_MAX && !submitting;
  return (
    <div
      className="scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) close();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label="请说明拒绝理由">
        <h3>请说明拒绝理由</h3>
        <div className="field">
          <textarea
            className="textarea"
            autoFocus
            maxLength={REJECT_MAX}
            placeholder="详细说明你不同意取消的理由(10-500 字)"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, REJECT_MAX))}
          />
          <div className="row between mt-2">
            <span className="hint" style={{ color: trimmed < REJECT_MIN ? 'var(--color-destructive)' : undefined }}>
              {trimmed}/{REJECT_MAX}(最少 {REJECT_MIN})
            </span>
          </div>
        </div>
        <div className="row gap-4 mt-6" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" disabled={submitting} onClick={close}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit}
            onClick={() => onSubmit(text.trim())}
          >
            {submitting ? '提交中…' : '提交'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── 小组件 / 工具 ─────────────── */
function FooterHint({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="sm muted"
      style={{
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--color-secondary)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {children}
    </p>
  );
}

function SellerResponseDeadline({ deadlineAt }: { deadlineAt: string }) {
  const { secondsLeft, isExpired } = useCountdown(deadlineAt);
  if (isExpired) {
    return <p className="xs muted mt-2">已超时(系统自动同意)</p>;
  }
  const urgent = secondsLeft < 3600;
  return (
    <p className="xs mt-2" style={{ color: urgent ? '#D14747' : 'var(--color-muted-foreground)' }}>
      请在 {formatDaysHoursMinutes(secondsLeft)} 内响应
    </p>
  );
}

/**
 * 走查修复:买家侧「取消/售后单记录」卡片。买家提交后能看到自己填的原因/说明/凭证,
 * 以及卖家响应截止倒计时(修"提交售后后看不到售后单记录")。凭证 URL 后端读时已现签 GET。
 */
function BuyerCancellationRecordCard({
  ctx,
  variant,
}: {
  ctx: CancellationContext;
  variant: 'aftersale' | 'cancel';
}) {
  return (
    <div className="card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
      <div className="sm" style={{ fontWeight: 'var(--weight-medium)' }}>
        {variant === 'aftersale' ? '售后单' : '取消申请'} · 原因:{REASON_LABELS[ctx.reasonCode] ?? '其他'}
      </div>
      {ctx.description && <p className="sm muted mt-2">{ctx.description}</p>}
      {ctx.evidenceUrls && ctx.evidenceUrls.length > 0 && (
        <div className="row gap-3 wrap mt-3">
          {ctx.evidenceUrls.map((url, i) => (
            <img
              key={url}
              src={url}
              alt={`凭证 ${i + 1}`}
              style={{
                width: 72,
                height: 72,
                objectFit: 'cover',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
              }}
            />
          ))}
        </div>
      )}
      <BuyerResponseDeadline deadlineAt={ctx.deadlineAt} />
    </div>
  );
}

function BuyerResponseDeadline({ deadlineAt }: { deadlineAt: string }) {
  const { secondsLeft, isExpired } = useCountdown(deadlineAt);
  if (isExpired) {
    return <p className="xs muted mt-2">卖家响应已超时,系统将自动处理</p>;
  }
  return <p className="xs muted mt-2">卖家需在 {formatDaysHoursMinutes(secondsLeft)} 内响应</p>;
}

function heroText(detail: OrderDetail): string {
  if (detail.subState === 'PENDING_SELLER_RESPONSE') return '待响应取消';
  if (detail.subState === 'IN_ARBITRATION') return '仲裁中';
  switch (detail.state) {
    case 'PENDING_PAYMENT':
      return '待支付';
    case 'IN_PROGRESS':
      return '进行中';
    case 'COMPLETED':
      return '已完成';
    case 'CANCELLED':
      return '已取消';
    default:
      return '未知';
  }
}

/** 仲裁 SLA:render 时算一次(不 tick),缺失退化「≤7 天」。 */
function arbitrationText(slaDeadlineAt: string | undefined, prefix: string): string {
  if (!slaDeadlineAt) return `${prefix}(≤ 7 天裁决)`;
  const ms = Date.parse(slaDeadlineAt) - Date.now();
  if (Number.isNaN(ms)) return `${prefix}(≤ 7 天裁决)`;
  if (ms <= 0) return `${prefix}(已超裁决截止,等待平台处理)`;
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 0) return `${prefix}(距裁决截止还有 ${days} 天 ${hours} 小时)`;
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (hours > 0) return `${prefix}(距裁决截止还有 ${hours} 小时 ${minutes} 分钟)`;
  return `${prefix}(距裁决截止还有 ${minutes} 分钟)`;
}

function formatDaysHours(seconds: number): string {
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3_600);
  return `${d} 天 ${h} 小时`;
}

function formatDaysHoursMinutes(seconds: number): string {
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3_600);
  const m = Math.floor((seconds % 3_600) / 60);
  if (d > 0) return `${d} 天 ${h} 小时`;
  if (h > 0) return `${h} 小时 ${m} 分钟`;
  return `${m} 分钟`;
}

function mapSellerResolveError(err: unknown): string {
  switch (errorCode(err)) {
    case '030103':
      return '订单当前状态不允许此操作(页面已过期)';
    case '030301':
      return '订单不存在';
    case '030302':
      return '你无权操作此订单';
    case '030505':
      return '取消申请不存在';
    case '030507':
    case '030509':
      return '取消申请已被处理(页面已过期)';
    case '030508':
      return '拒绝理由长度需在 10-500 字符之间';
    case '030101':
      return '订单状态冲突,请重新加载';
    default:
      return errorMessage(err, '提交失败,请重试');
  }
}
